import Network from '@Networks/Localhost';
import LeaderboardError from './LeaderboardError';
import LeaderboardEntry, { LeaderboardEntryExportDataObject } from '@Leaderboards/LeaderboardEntry';

/**-------------------- Errors ----------------------*/

const ERRORS = {
    NO_CONTEXT_FOUND: 'NO_CONTEXT_FOUND',
    NO_NETWORK_LEADERBOARD: 'NO_NETWORK_LEADERBOARD'
}

const PLAYER_DATA_KEY = 'LEADERBOARDS';
const PLAYER_DATA_EXPIRE_TIME = (1000 * 60);

/**------------------ Leaderboard --------------------*/

/**
 * Leaderboard
 * This is an cached verson of a leaderboard
 * This will aid in reducing rate limited network calls
 */
class Leaderboard implements Networks.Leaderboard
{
    /**
     * Leaderboard ID
     */
    private id: string;

    /**
     * Contextual
     * Contextual leaderboards belong to a specific context (message)
     */
    private contextual: boolean;

    /**
     * Player
     */
    private player: Networks.Player;

    /**
     * Reference to the network leaderboard
     */
    private networkLeaderboard: Networks.Leaderboard;

    /**
     * Cached leaderboard entries
     */
    private leaderboardEntries: Networks.LeaderboardEntry[];

    /**
     * Cached connected leaderboard entries
     */
    private connectedLeaderboardEntires: Networks.LeaderboardEntry[];

    /**
     * Cached Player leaderboard entry
     */
    private playerEntry: Networks.LeaderboardEntry;

    /**
     * Current entry count
     */
    private entryCount: number;

    /**
     * Constructor
     */
    constructor(leaderboardID: string, contextual: boolean = false)
    {
        this.id = leaderboardID;
        this.contextual = contextual;
    }

    /**
     * Initializes leaderboard
     */
    public async initializeAsync()
    {

        this.playerEntry = null;
        this.leaderboardEntries = null;
        this.connectedLeaderboardEntires = null;

        await Promise.all([

            /**
             * Fetch leaderbaord from network
             */
            this.fetchNetworkLeaderboard(),

            /**
             * Read leaderboard from cloud storage
             */
            this.readNetworkPlayerLeaderboardEntryFromCloudStorage(),
            this.readNetworkLeaderboardEntriesFromCloudStorage(),
            this.readNetworkConnectedLeaderboardEntriesFromCloudStorage()
        ]);
    }

    /**
     * Checks if leaderboard is contextual
     * @returns - whether or not the leaderboard is contextual
     */
    public isContextual(): boolean
    {
        return this.contextual;
    }

    /**
     * Fetches network leaderboard
     * @throws NO_CONTEXT_FOUND
     */
    private async fetchNetworkLeaderboard()
    {
        const leaderboardID = this.getID();

        this.networkLeaderboard = await Network.getLeaderboardAsync(leaderboardID);
    }

    /*-------------------------- GENERIC CLOUD STORAGE READ/WRITE --------------------------------*/

    /**
     * Reads leaderboard entries from cloud storage
     * @param key
     * @returns leaderboard entries
     */
    private async readLeaderboardEntriesFromCloudStorage(dataKey: string): Promise<Networks.LeaderboardEntry[]>
    {
        const playerData = await Network.player.getDataAsync([dataKey]);

        const leaderboardEntries = [];

        if (playerData.hasOwnProperty(dataKey))
        {
            const playerLeaderboardData = playerData[dataKey];

            if (Date.now() < playerLeaderboardData.timestamp + PLAYER_DATA_EXPIRE_TIME)
            {
                const leaderboardEntriesData = playerLeaderboardData.entries as LeaderboardEntryExportDataObject[]

                for (const leaderbardEntry of leaderboardEntriesData)
                {
                    leaderboardEntries.push(new LeaderboardEntry(leaderbardEntry));
                }
            }
        }

        return leaderboardEntries;
    }

    /**
     *
     * @param dataKey - key to save to
     * @param leaderboardEntries - entries to export and save
     */
    private async writeLeaderboardEntriesToCloudStorage(dataKey: string, leaderboardEntries: Networks.LeaderboardEntry[]): Promise<void>
    {
        const leaderboardExportData = [];

        for (const leaderboardEntry of leaderboardEntries)
        {
            const exportedLeaderboardEntryData = LeaderboardEntry.export(leaderboardEntry);

            leaderboardExportData.push(exportedLeaderboardEntryData);
        }

        if (leaderboardExportData.length > 0)
        {
            await Network.player.setDataAsync({
                [dataKey]: {
                    entries: leaderboardExportData,
                    timestamp: Date.now()
                }
            });
        }
    }

    /*----------------------- PLAYER ENTRY CLOUD STORAGE  --------------------------------*/

    /**
    * Fetch network player leaderboardEntry to cloud storage
    */
    private async readNetworkPlayerLeaderboardEntryFromCloudStorage()
    {
        const leaderboardID = this.getID();

        const playerDataLeaderboardKey = `${PLAYER_DATA_KEY}-${leaderboardID}-player-entry`;

        const playerEntry = await this.readLeaderboardEntriesFromCloudStorage(playerDataLeaderboardKey);

        if (playerEntry.length > 0)
        {
            this.playerEntry = playerEntry[0];
        }
    }

    /**
     * Sets network player leaderboard entry to cloud storage
     */
    private async writeNetworkPlayerLeaderboardEntryToCloudStorage()
    {
        if (this.playerEntry)
        {
            const leaderboardID = this.getID();

            const playerDataLeaderboardKey = `${PLAYER_DATA_KEY}-${leaderboardID}-player-entry`;

            await this.writeLeaderboardEntriesToCloudStorage(playerDataLeaderboardKey, [this.playerEntry]);
        }
    }

    /*-------------------- GENERAL LEADERBOARD CLOUD STORAGE   ----------------------------*/

    /**
     * Reads network leaderboard entries
     */
    private async readNetworkLeaderboardEntriesFromCloudStorage()
    {
        const leaderboardID = this.getID();

        const playerDataLeaderboardKey = `${PLAYER_DATA_KEY}-${leaderboardID}-entries`;

        const leaderboardEntries = await this.readLeaderboardEntriesFromCloudStorage(playerDataLeaderboardKey);

        if (leaderboardEntries.length > 0)
        {
            this.leaderboardEntries = leaderboardEntries;
        }

    }

    /**
     * Writes network leaderboard entries to cloud storage
     */
    private async writeNetworkLeaderboardEntriesToCloudStorage()
    {
        const leaderboardID = this.getID();

        if (this.leaderboardEntries)
        {
            const leaderboardEntries = this.leaderboardEntries;

            const playerDataLeaderboardKey = `${PLAYER_DATA_KEY}-${leaderboardID}-entries`;

            await this.writeLeaderboardEntriesToCloudStorage(
                playerDataLeaderboardKey,
                leaderboardEntries
            );
        }
    }

    /*-------------------- FRIENDS LEADERBOARD CLOUD STORAGE   ----------------------------*/

    /**
     * Reads network leaderboard entries
     */
    private async readNetworkConnectedLeaderboardEntriesFromCloudStorage()
    {
        const leaderboardID = this.getID();

        const playerDataLeaderboardKey = `${PLAYER_DATA_KEY}-${leaderboardID}-friends-entries`;

        const leaderboardEntries = await this.readLeaderboardEntriesFromCloudStorage(playerDataLeaderboardKey);

        if (leaderboardEntries.length > 0)
        {
            this.connectedLeaderboardEntires = leaderboardEntries;
        }
    }

    /**
     * Writes network leaderboard entries to cloud storage
     */
    private async writeNetworkConnectedLeaderboardEntriesToCloudStorage()
    {
        const leaderboardID = this.getID();

        if (this.connectedLeaderboardEntires)
        {
            const leaderboardEntries = this.connectedLeaderboardEntires;

            const playerDataLeaderboardKey = `${PLAYER_DATA_KEY}-${leaderboardID}-friends-entries`;

            await this.writeLeaderboardEntriesToCloudStorage(
                playerDataLeaderboardKey,
                leaderboardEntries,
            );
        }
    }

    /*-------------------------------------------------------------------------------------*/

    /**
     * Checks if network leaderboard is found, throws error if not.
     * @throws NO_NETWORK_LEADERBORD
     */
    private checkNetworkLeaderboard()
    {
        if (!this.networkLeaderboard)
        {
            throw new LeaderboardError(ERRORS.NO_NETWORK_LEADERBOARD, 'No network leaderboard found, you must fetch the network leaderboard first')
        }
    }

    /**
     * Returns the ID of the leaderboard including context ID
     */
    public getID(): string
    {
        let leaderboardID = this.id;

        if (this.contextual)
        {
            if (Network.context.getID())
            {
                leaderboardID += '.' + Network.context.getID();
            }
            else
            {
                throw new LeaderboardError(ERRORS.NO_CONTEXT_FOUND, 'Contextual leaderboard cannot be fetched without a context');
            }
        }

        return leaderboardID;
    }

    /**
     * The name of the leaderboard.
     */
    public getName(): string
    {
        this.checkNetworkLeaderboard();

        return this.networkLeaderboard.getName();
    }

    /**
     * The ID of the context that the leaderboard is associated with, or null if the leaderboard is not tied to a particular context.
     */
    public getContextID(): string | null
    {
        this.checkNetworkLeaderboard();

        return this.networkLeaderboard.getContextID();
    }

    /**
     * Fetches the total number of player entries in the leaderboard.
     *
     * @returns  A unique identifier for the player.
     * @throws NETWORK_FAILURE
     * @throws RATE_LIMITED
     */
    public async getEntryCountAsync(): Promise<number>
    {
        this.checkNetworkLeaderboard();

        let entryCount = this.entryCount;

        if (!entryCount)
        {
            entryCount = await this.networkLeaderboard.getEntryCountAsync();
        }

        return entryCount;
    }

    /**
     * Updates the player's score. If the player has an existing score, the old score will only be replaced if the new score is better than it.
     * NOTE: If the leaderboard is associated with a specific context, the game must be in that context to set a score for the player.
     *
     * @param score The new score for the player. Must be a 64-bit integer number.
     * @param extraData Metadata to associate with the stored score. Must be less than 2KB in size.
     * @returns Resolves with the current leaderboard entry for the player after the update.
     * @throws LEADERBOARD_WRONG_CONTEXT
     * @throws NETWORK_FAILURE
     * @throws INVALID_PARAM
     * @throws INVALID_OPERATION
     * @throws RATE_LIMITED
     */
    public async setScoreAsync(score: number, extraData?: string): Promise<Networks.LeaderboardEntry>
    {
        this.checkNetworkLeaderboard();

        if (!this.playerEntry || this.playerEntry && this.playerEntry.getScore() < score)
        {
            this.playerEntry = await this.networkLeaderboard.setScoreAsync(score, extraData);

            await this.writeNetworkPlayerLeaderboardEntryToCloudStorage();

            /**
             * Sort leaderboard and write it back to storage
             */
            if (this.leaderboardEntries && this.leaderboardEntries.length > 0)
            {
                this.leaderboardEntries = this.sortLeaderboardEntries(this.leaderboardEntries);

                await this.writeNetworkLeaderboardEntriesToCloudStorage();
            }

            /**
             * Sort connected leaderboard and write it back to storage
             */
            if (this.connectedLeaderboardEntires && this.connectedLeaderboardEntires.length > 0)
            {
                this.connectedLeaderboardEntires = this.sortLeaderboardEntries(this.connectedLeaderboardEntires);

                await this.writeNetworkConnectedLeaderboardEntriesToCloudStorage();
            }

        }

        return this.playerEntry;
    }

    /**
     * Recalculates leaderboard
     * This is required so that we don't make another request to the network.
     */
    private sortLeaderboardEntries(leaderboardEntries: Networks.LeaderboardEntry[]): Networks.LeaderboardEntry[]
    {
        const playerID = Network.player.getID();

        /**
         * Filter out current player, as we're going to replace it
         */
        const sortedLeaderboard = leaderboardEntries.filter((entry) =>
        {
            return entry.getPlayer().getID() !== playerID;
        });

        /**
         * Add player entry to leaderboard
         */
        if (this.playerEntry)
        {
            sortedLeaderboard.push(this.playerEntry)
        }

        sortedLeaderboard.sort((leaderboardEntryA, leaderboardEntryB) =>
        {
            return leaderboardEntryB.getScore() - leaderboardEntryA.getScore();
        });

        /**
         * Create new leaderboard entries.
         */
        const newLeaderboard = sortedLeaderboard.map((leaderboardEntry, index) =>
        {
            const leaderboardEntryData = LeaderboardEntry.export(leaderboardEntry);
            leaderboardEntryData.rank = index + 1;
            return new LeaderboardEntry(leaderboardEntryData);
        });

        return newLeaderboard;
    }


    /**
     * Retrieves the leaderboard's entry for the current player, or null if the player has not set one yet.
     *
     * @returns Resolves with the current leaderboard entry for the player.
     * @throws NETWORK_FAILURE
     * @throws INVALID_OPERATION
     * @throws RATE_LIMITED
     */
    public async getPlayerEntryAsync(): Promise<Networks.LeaderboardEntry | null>
    {
        this.checkNetworkLeaderboard();

        if (!this.playerEntry)
        {
            this.playerEntry = await this.networkLeaderboard.getPlayerEntryAsync();

            await this.writeNetworkPlayerLeaderboardEntryToCloudStorage();
        }

        return this.playerEntry;
    }

    /**
     * Retrieves a set of leaderboard entries, ordered by score ranking in the leaderboard.
     *
     * @param count The number of entries to attempt to fetch from the leaderboard. Defaults to 10 if not specified. Up to a maximum of 100 entries may be fetched per query.
     * @param offset The offset from the top of the leaderboard that entries will be fetched from.
     * @returns Resolves with the leaderboard entries that match the query.
     * @throws NETWORK_FAILURE
     * @throws RATE_LIMITED
     */
    public async getEntriesAsync(count: number, offset: number): Promise<Networks.LeaderboardEntry[]>
    {
        this.checkNetworkLeaderboard();

        /**
         * We're going to ignore the count and offset for now
         * We can change it if needed
         */
        if (!this.leaderboardEntries)
        {
            this.leaderboardEntries = await this.networkLeaderboard.getEntriesAsync(count, offset);

            this.entryCount = this.leaderboardEntries.length;

            await this.writeNetworkLeaderboardEntriesToCloudStorage();
        }

        return this.leaderboardEntries;
    }

    /**
     * Retrieves the leaderboard score entries of the current player's connected players (including the current player), ordered by local rank within the set of connected players.
     * @param count  The number of entries to attempt to fetch from the leaderboard. Defaults to 10 if not specified. Currently, up to a maximum of 100 entries may be fetched per query.
     * @param offset The offset from the set of ordered connected player score entries to fetch from.
     * @returns Resolves with the leaderboard entries that match the query.
     */
    public async getConnectedPlayerEntriesAsync(count: number, offset: number): Promise<Networks.LeaderboardEntry[]>
    {
        this.checkNetworkLeaderboard();

        if (!this.connectedLeaderboardEntires)
        {
            this.connectedLeaderboardEntires = await this.networkLeaderboard.getConnectedPlayerEntriesAsync(5, 0);

            await this.writeNetworkConnectedLeaderboardEntriesToCloudStorage();
        }

        return this.connectedLeaderboardEntires;
    }
}

export default Leaderboard;